import React from 'react'
import { localized } from 'decorators'
import { LoadingPageContent } from 'components/loading'
import { ErrorViewer } from 'components/error'
import { history } from 'store/store'
import { Page } from 'components/page'
import { iconsCreator } from 'utils/iconsCreator'
import { Button, ConfirmButton } from 'components/buttons'
import { useMountEffect } from 'utils/useMountEffect'
import { CheckBox } from 'components/checkbox'
import { ValidationBoundary, ValidationResult, ErrorValidationIcon } from 'components/validation'
import { useStyles } from './detailPage.css'
import { usePersistence } from 'decorators'

const saveAndGoToList = (props, entity, goToList) => {
  const callback = goToList 
    ? () => { history.push(props.metadata.urls.base) }
    : props.afterSaveCallback
  props.save(entity, callback)
}

const saveEntity = (canSave, props, goToList) => () => {
  if (canSave) {
    saveAndGoToList(props, props.entity, goToList)
  }
}
const saveEntityAsNew = (canSave, props, goToList) => () => {
  if (canSave) { 
    saveAndGoToList(props, { ...props.entity,  ...(props.metadata.saveAsNewDataFlag || { id: 0 }) }, goToList)
  }
}
const restoreEntity = (canRestore, props) => () => {
  if (canRestore) { 
    props.restore(
      props.entity, 
      () => {
        if (props.afterRestoreCallback) { props.afterRestoreCallback() }
        history.push(props.backUrl)
      }
    )
  }
}
const deleteEntity = (props) => () => {
  if (typeof props.delete === 'function') { 
    props.delete(props.entity, (e) => {
      if (props.afterDeleteCallback) { props.afterDeleteCallback(e) }
      if (!props.hideGoBack && (props.metadata.backUrl || props.metadata.urls.base)) { history.push((props.metadata.backUrl || props.metadata.urls.base)) }
    })
  }
}
const getConfirmMessage = (props) => {
  if (props.entity) { return props.confirmDeleteMessage(props.entity) }
  return null
}

const DetailPage = (props) => {
  const classes = useStyles()
  const { get: getGoToList, set: setGoToList } = usePersistence(`page_goBackAfterSave:${props.metadata.page}`)
  const [checked, setChecked] = React.useState(getGoToList(false))
  const handleCheckboxChange = React.useCallback((ch) => {
    setChecked(ch)
    setGoToList(ch)
  }, [setGoToList, setChecked])

  useMountEffect(() => {
    if (typeof props.getEntity === 'function') {
      props.getEntity()
    }
  })
  
  const renderForm = () => {
    const { entity, fetching } = props
    if (fetching) { return <LoadingPageContent />}
    if (error !== null) { return <ErrorViewer onClick={() => props.getEntity()} />}
    if (entity === null) { return <LoadingPageContent />}

    return props.children
  }

  const { localize } = props
  const { metadata, entity, saving, fetching, error } = props
  
  const canSave = (typeof props.save === 'function')
  const canRestore = (typeof props.restore === 'function')

  const isSaveAsNewDisabled = !canSave || saving || (entity === null)
  const isDisabled = isSaveAsNewDisabled || (entity || {}).isDirty !== true
  const canSaveAsNew = canSave && metadata.enableSaveAsNew && (entity || {}).id > 0
  const canBeDeleted = (entity !== null && entity.canBeDeleted !== false)
  return (
    <ValidationBoundary>
      <Page
        loading={fetching}
        error={error}
        title={localize(`${metadata.page}.detailTitle`)}
        metadata={metadata}
        titleButtons={<ValidationResult render={({ isValid }) => 
          <React.Fragment>
            {props.beforeHeaderButtonsContent}
            <CheckBox
              checked={checked}
              onChange={handleCheckboxChange}
              title={localize('common.saveAndGoToList')}
            />
            {canSaveAsNew && <div className={classes.saveAsNewButton}>
              <Button
                disabled={isSaveAsNewDisabled || !isValid || props.deleting}
                isLoading={saving}
                width={230}
                onClick={saveEntityAsNew(canSave, props, checked)}
                color={props.saveButtonColor || 'primary'}
                title={localize(metadata.saveAsNewTooltipKey || 'common.saveAsNewTooltip')}
                icon={metadata.saveAsNewIcon || props.saveButtonIcon || 'save'}>{props.saveButtonTitle || localize(metadata.saveAsNewTextKey || 'common.saveAsNew')}</Button>
                <ErrorValidationIcon isValid={isValid} title={localize('common.formContainsValidationErrors')} backgroundColor='transparent' iconProps={{ style: { fontSize: 16 } }} />
              </div>}
            {props.confirmDeleteMessage && <ConfirmButton
              className={classes.headerButtons}
              disabled={(props.deleting || !props.entity || props.entity.id === 0 || !canBeDeleted)}
              isLoading={props.deleting}
              color='secondary'
              confirmMessage={getConfirmMessage(props)}
              title={props.confirmDeleteTooltip}
              onConfirm={deleteEntity(props)}
            >{localize(props.deleteButtonTitle || 'common.delete')}</ConfirmButton>}
            
            {canSave && <div className={classes.saveButton}>
              <Button
                disabled={isDisabled || !isValid || props.deleting}
                isLoading={saving}
                onClick={saveEntity(canSave, props, checked)}
                color={props.saveButtonColor || 'primary'}
                icon={props.saveButtonIcon || 'save'}>{props.saveButtonTitle || localize('common.save')}</Button>
                <ErrorValidationIcon isValid={isValid} title={localize('common.formContainsValidationErrors')} backgroundColor='transparent' iconProps={{ style: { fontSize: 16 } }} />
              </div>}
            {canRestore && <Button
              disabled={props.restoring}
              isLoading={props.restoring}
              onClick={restoreEntity(canRestore, props)}
              color='primary'
              title={localize('common.restoreFromTrash')}
              icon={iconsCreator('RefreshIcon')}>{localize('common.restore')}</Button>}
          </React.Fragment>} />}>
        <div className={classes.root}>{renderForm()}</div>
      </Page>
    </ValidationBoundary>
  )
}

export default React.memo(localized(DetailPage))